import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Menu, X, BookOpen, Mail, User, Video, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

// New transparent PNG logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/xykys01p_Omlight%20Logo%20PNG.png";
const INGRAM_LINK = "https://shop.ingramspark.com/b/084?params=WOjJaIkWSWaOGLiXcoDCT2QxXTvD81n1g0bmkVhVhbs";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [showLogoOverlay, setShowLogoOverlay] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { href: "#", label: "Home", icon: Home, isHome: true },
    { href: "#book", label: "The Book", icon: BookOpen },
    { href: "#author", label: "Meet Linda", icon: User },
    { href: "#reflections", label: "Media Content", icon: Video },
    { href: "#contact", label: "Contact", icon: Mail },
  ];

  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#2D3E4A]/95 backdrop-blur-xl shadow-lg"
          : "bg-[#2D3E4A]/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - click to show centered overlay */}
          <button
            data-testid="nav-logo"
            className="flex items-center gap-3 relative"
            onClick={() => setShowLogoOverlay(true)}
            onMouseEnter={() => setIsLogoHovered(true)}
            onMouseLeave={() => setIsLogoHovered(false)}
          >
            <div className="relative overflow-visible">
              <img
                src={LOGO_URL}
                alt="Omlight Publications"
                className={`h-14 w-auto drop-shadow-lg transition-all duration-500 ease-out origin-left ${
                  isLogoHovered ? "scale-125" : "scale-100"
                }`}
              />
              {/* Subtle glow effect on hover */}
              <div 
                className={`absolute inset-0 bg-[#D4AF37]/20 blur-xl rounded-full transition-opacity duration-500 ${
                  isLogoHovered ? "opacity-100" : "opacity-0"
                }`}
                style={{ transform: 'scale(1.5)' }}
              />
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href + link.label}
                href={link.href}
                onClick={link.isHome ? scrollToTop : undefined}
                data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                className="font-serif text-base text-[#D4AF37] hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href={INGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-order-button"
            >
              <Button className="btn-gold font-serif text-base shadow-lg">
                Order Book
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#2D3E4A]/95 backdrop-blur-xl absolute top-20 left-0 right-0 border-t border-white/10 shadow-lg"
        >
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.href + link.label}
                href={link.href}
                onClick={(e) => {
                  if (link.isHome) {
                    scrollToTop(e);
                  }
                  setIsMobileMenuOpen(false);
                }}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                className="flex items-center gap-3 py-2 font-serif text-base text-[#D4AF37] hover:text-white"
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </a>
            ))}
            <a
              href={INGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="btn-gold font-serif text-base w-full mt-2">
                Order Book
              </Button>
            </a>
          </div>
        </div>
      )}

      {/* Logo Overlay Modal - Portal to body for proper stacking */}
      {showLogoOverlay && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md"
          onClick={() => setShowLogoOverlay(false)}
          data-testid="logo-overlay-modal"
        >
          <div 
            className="relative bg-gradient-to-br from-[#2D3E4A] to-[#1A2530] rounded-2xl p-6 sm:p-8 max-w-sm w-[90%] sm:w-full border border-[#D4AF37]/30 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowLogoOverlay(false)}
              className="absolute top-3 right-3 p-2 text-white/60 hover:text-white transition-colors z-10"
              data-testid="logo-overlay-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Centered Content */}
            <div className="flex flex-col items-center text-center pt-2">
              {/* Large Expanded Logo with glow */}
              <div className="mb-4 relative">
                <div className="absolute inset-0 bg-[#D4AF37]/20 blur-2xl rounded-full" />
                <img
                  src={LOGO_URL}
                  alt="Omlight Publications Logo"
                  className="relative h-24 sm:h-28 w-auto drop-shadow-xl"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.3))' }}
                />
              </div>

              <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">
                Omlight Publications
              </h2>

              <p className="font-sans text-xs text-[#D4AF37] tracking-widest uppercase mb-3">
                Illuminating Hearts & Minds
              </p>

              <div className="w-12 h-px bg-[#D4AF37]/50 mb-3" />

              <p className="font-sans text-sm text-white/80 leading-relaxed mb-4">
                Omlight Publications is dedicated to sharing stories of spiritual awakening, 
                transformation, and the rediscovery of one's inner light. Through thoughtful 
                works like <em className="text-[#F8C471]">The Distant Lighthouse</em>, we 
                illuminate the path back to remembrance.
              </p>

              <p className="text-white/50 text-xs">
                Founded by Linda D. Gallagher
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navigation;
